/**
 * Microsoft Entra ID (Azure AD) authentication for the Meridian SPA.
 *
 * Uses MSAL browser with the POPUP flow: login happens on a click-initiated
 * popup, so no redirect routes are needed and the SPA keeps its current
 * routing. Tokens are requested for the Meridian orchestrator API scope and
 * attached as 'Authorization: Bearer' by apiFetch(). Password (cookie) login
 * remains fully supported during the migration.
 */

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';

const ENTRA_CLIENT_ID = 'a02a4f0d-a759-4dcb-93cb-1f21c966e32f';
const ENTRA_TENANT_ID = 'e0118ef7-a625-48fa-ac9f-1305a9b34a63';
export const ENTRA_API_SCOPE = `api://${ENTRA_CLIENT_ID}/access_as_user`;

let pca: PublicClientApplication | null = null;
let initPromise: Promise<PublicClientApplication> | null = null;

export function entraInstance(): PublicClientApplication {
  if (!pca) {
    pca = new PublicClientApplication({
      auth: {
        clientId: ENTRA_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${ENTRA_TENANT_ID}`,
        redirectUri: window.location.origin,
      },
      cache: { cacheLocation: 'sessionStorage' },
    });
  }
  return pca;
}

/**
 * MSAL-browser v3 requires initialize() to settle before any interactive or
 * silent call — otherwise it throws uninitialized_public_client_application.
 * Ensure the app is created and initialized exactly once, then reuse it.
 */
async function entraReady(): Promise<PublicClientApplication> {
  const app = entraInstance();
  if (!initPromise) initPromise = app.initialize().then(() => app);
  return initPromise;
}

/** Sign in with Microsoft (popup) and return the API access token. */
export async function entraLogin(): Promise<string> {
  const app = await entraReady();
  const result = await app.loginPopup({ scopes: [ENTRA_API_SCOPE] });
  return result.accessToken;
}

/** Get a valid API token silently; null when no Entra session exists. */
export async function getEntraToken(): Promise<string | null> {
  try {
    const app = await entraReady();
    const accounts: AccountInfo[] = app.getAllAccounts();
    if (accounts.length === 0) return null;
    const result = await app.acquireTokenSilent({
      scopes: [ENTRA_API_SCOPE],
      account: accounts[0],
    });
    return result.accessToken;
  } catch (err) {
    // No session / expired interaction: not an error, just no token.
    if (err instanceof InteractionRequiredAuthError) return null;
    return null;
  }
}

/** Sign out of the Enta session if one exists. */
export async function entraLogout(): Promise<void> {
  try {
    const app = await entraReady();
    const accounts = app.getAllAccounts();
    if (accounts.length > 0) {
      await app.logoutPopup({ account: accounts[0] });
    }
  } catch {
    // best-effort: the local session cache clears below regardless
  }
}
